import { z } from "zod";
import {
  bytesHash,
  newStatementDataset,
  parseProject,
  statementFieldTypes,
  type Project,
} from "@proof/core";

const sqlId = z.number().int().positive().max(2147483647);
const catalogSchema = z
  .object({
    groupId: sqlId,
    name: z.string().min(1).max(200),
    statements: z
      .array(
        z
          .object({
            id: sqlId,
            name: z.string().min(1).max(200),
            text: z.string().max(32768),
          })
          .strict(),
      )
      .max(10000),
  })
  .strict();
export type LegacyStatementCatalog = z.infer<typeof catalogSchema>;

/** Packaged statement rows contain names, not content or membership. Hosts supply those explicitly. */
export function bindLegacyStatementCatalogs(
  project: Project,
  input: readonly LegacyStatementCatalog[] = [],
) {
  const sources = [
    ...project.fields.map((field) => field.source),
    ...project.parts.flatMap((part) =>
      Object.values(part.overrides).flatMap((patch) =>
        patch.source ? [patch.source] : [],
      ),
    ),
  ];
  if (
    !sources.some((source) => source.kind === "statement" && !source.datasetId)
  )
    return { project, warnings: [] as string[] };
  const catalogs = z.array(catalogSchema).max(100).parse(input),
    p = parseProject(project),
    warnings: string[] = [];
  let budget = 998000;
  for (const catalog of catalogs) {
    budget -= new TextEncoder().encode(catalog.name).length + 100;
    for (const item of catalog.statements) {
      budget -= new TextEncoder().encode(JSON.stringify(item)).length + 1;
      if (budget < 0) throw new Error("Legacy statement catalogs exceed 1 MB");
    }
  }
  if (
    new Set(catalogs.map((catalog) => catalog.groupId)).size !== catalogs.length
  )
    throw new Error("Duplicate legacy statement groups");
  for (const catalog of catalogs)
    if (
      new Set(catalog.statements.map((item) => item.id)).size !==
      catalog.statements.length
    )
      throw new Error("Duplicate legacy statement IDs");
  const bind = (
    source: Project["fields"][number]["source"],
    type: Project["fields"][number]["type"],
  ) => {
    if (source.kind !== "statement" || source.datasetId) return;
    if (
      !statementFieldTypes.includes(
        type as (typeof statementFieldTypes)[number],
      )
    ) {
      warnings.push(
        `Statement group ${source.key || "(unbound)"}: control ${type} is retained without binding native text content`,
      );
      return;
    }
    const catalog = catalogs.find(
      (item) => String(item.groupId) === source.key,
    );
    if (
      !catalog ||
      (source.statementId &&
        !catalog.statements.some(
          (item) => String(item.id) === source.statementId,
        ))
    ) {
      warnings.push(
        `Statement group ${source.key || "(unbound)"}: supply explicit host content and membership, including the default statement`,
      );
      return;
    }
    const hash = bytesHash(
        new TextEncoder().encode(JSON.stringify(catalog)),
      ).slice(0, 16),
      datasetId = `statement_group_${catalog.groupId}_${hash}`,
      dataset = {
        ...newStatementDataset(datasetId, catalog.name),
        rows: catalog.statements.map((item) => ({
          id: String(item.id),
          name: item.name,
          content: item.text,
        })),
      };
    if (!p.datasets.some((item) => item.id === datasetId))
      p.datasets.push(dataset);
    Object.assign(source, {
      datasetId,
      datasetVersion: 1,
      resultColumn: "content",
      labelColumn: "name",
    });
  };
  for (const field of p.fields) bind(field.source, field.type);
  for (const part of p.parts)
    for (const [fieldId, patch] of Object.entries(part.overrides))
      if (patch.source)
        bind(
          patch.source,
          patch.type || p.fields.find((field) => field.id === fieldId)!.type,
        );
  return { project: parseProject(p), warnings: [...new Set(warnings)] };
}
